import { m360Paths } from "@/lib/m360Paths";

export const WRITE_PROPOSAL_PREFIX = "MCP_WRITE_PROPOSAL:";

export type McpWriteProposal = {
  tool: string;
  arguments: Record<string, unknown>;
  /** Signed one-time propose-step token from the Backoffice agent BFF. */
  token: string;
};

export type WriteProposalDetailRow = {
  label: string;
  value: string;
  href?: string;
};

export function writeProposalReviewLabel(tool: string): string {
  switch (tool) {
    case "create_person_note":
      return "staff note proposal";
    case "create_interaction":
      return "CRM interaction proposal";
    case "create_task":
      return "CRM task proposal";
    default:
      return "write proposal";
  }
}

export function parseWriteProposal(text: string): McpWriteProposal | null {
  const index = text.lastIndexOf(WRITE_PROPOSAL_PREFIX);
  if (index === -1) {
    return null;
  }

  try {
    const parsed = JSON.parse(text.slice(index + WRITE_PROPOSAL_PREFIX.length)) as Partial<McpWriteProposal>;
    if (
      !parsed?.tool ||
      typeof parsed.arguments !== "object" ||
      parsed.arguments === null ||
      Array.isArray(parsed.arguments) ||
      typeof parsed.token !== "string" ||
      parsed.token.trim() === ""
    ) {
      return null;
    }

    return {
      tool: parsed.tool,
      arguments: parsed.arguments,
      token: parsed.token
    };
  } catch {
    return null;
  }
}

export function stripWriteProposalMarker(text: string): string {
  const index = text.lastIndexOf(WRITE_PROPOSAL_PREFIX);
  if (index === -1) {
    return text;
  }

  return text.slice(0, index).trimEnd();
}

export function writeProposalKey(proposal: McpWriteProposal): string {
  return `${proposal.tool}:${JSON.stringify(proposal.arguments)}`;
}

/** Confirmation identity for one assistant message's proposal (allows same payload later). */
export function confirmedWriteProposalKey(messageId: string, proposal: McpWriteProposal): string {
  return `${messageId}:${writeProposalKey(proposal)}`;
}

/**
 * Human-readable rows staff review before Confirm. The signed `token` never
 * appears here — it is forwarded as `proposal_token` on execute_write only.
 */
const NOT_PROVIDED = "Not provided";

type ProposalFieldSpec = {
  key: string;
  label: string;
};

const PROPOSAL_FIELD_SPECS: Record<string, ProposalFieldSpec[]> = {
  create_person_note: [
    { key: "pga_id", label: "Member (PGA ID)" },
    { key: "title", label: "Title" },
    { key: "content", label: "Content" },
    { key: "priority", label: "Priority" },
    { key: "visibility", label: "Visibility" }
  ],
  create_interaction: [
    { key: "pga_id", label: "Member (PGA ID)" },
    { key: "mode", label: "Mode" },
    { key: "interaction_date", label: "Date" },
    { key: "categories", label: "Categories" },
    { key: "summary", label: "Summary" },
    { key: "description", label: "Description" },
    { key: "privacy_restricted", label: "Privacy restricted" },
    { key: "job_post_id", label: "Job post" }
  ],
  create_task: [
    { key: "pga_id", label: "Member (PGA ID)" },
    { key: "name", label: "Task" },
    { key: "description", label: "Description" },
    { key: "due_date", label: "Due date" },
    { key: "visibility", label: "Visibility" }
  ]
};

function formatDetailValue(value: unknown): string {
  if (value === null || value === undefined) {
    return NOT_PROVIDED;
  }

  if (typeof value === "string") {
    return value.trim() === "" ? NOT_PROVIDED : value;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    const items = value.map((item) => String(item)).filter((item) => item.trim() !== "");
    return items.length > 0 ? items.join(", ") : NOT_PROVIDED;
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

export function writeProposalDetailRows(proposal: McpWriteProposal): WriteProposalDetailRow[] {
  const specs = PROPOSAL_FIELD_SPECS[proposal.tool];
  if (!specs) {
    return Object.entries(proposal.arguments).map(([key, value]) => ({
      label: key,
      value: formatDetailValue(value)
    }));
  }

  const rows = specs.map(({ key, label }): WriteProposalDetailRow => {
    const value = proposal.arguments[key];
    if (key === "pga_id" && typeof value === "string" && value.trim() !== "") {
      return { label, value: value.trim(), href: m360Paths.personOverview(value.trim()) };
    }

    return { label, value: formatDetailValue(value) };
  });

  // Surface unexpected keys rather than silently dropping proposed writes.
  const knownKeys = new Set(specs.map((spec) => spec.key));
  for (const [key, value] of Object.entries(proposal.arguments)) {
    if (!knownKeys.has(key)) {
      rows.push({ label: key, value: formatDetailValue(value) });
    }
  }

  return rows;
}
